/**
 * RevertToPreliminaryModal
 * Modal for reverting an assignment from 'planerad' back to 'preliminar'.
 * Allows choosing customer status and SMS notifications to TGs.
 */

import { useState } from 'react'
import { X, AlertTriangle, Loader2, User, Leaf, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react'
import type { CustomerCommStatus, TgWorkStatus } from '../../types/statusTypes'

type CustomerStatusAction = 'keep' | 'reset_to_ej_kontaktad' | 'set_informerad'

interface TgInfo {
  id: string
  subcontractorId: string
  name: string
  phone: string | null
  currentStatus: TgWorkStatus
}

export interface RevertToPreliminaryResult {
  customerAction: CustomerStatusAction
  sendSmsTo: Record<string, boolean>  // keyed by subcontractor id
  extraMessages: Record<string, string>  // keyed by subcontractor id
}

interface RevertToPreliminaryModalProps {
  isOpen: boolean
  onClose: () => void
  assignmentNumber?: number | null
  assignmentTitle: string
  assignmentDate?: string
  startTime?: string | null
  customerName: string
  customerCurrentStatus: CustomerCommStatus
  tgList: TgInfo[]
  onConfirm: (result: RevertToPreliminaryResult) => Promise<void>
}

const CUSTOMER_STATUS_OPTIONS: { value: CustomerStatusAction; label: string; description: string }[] = [
  { value: 'keep', label: 'Behåll nuvarande status', description: 'Kunden har redan godkänt ändringen' },
  { value: 'reset_to_ej_kontaktad', label: 'Återställ till Ej kontaktad', description: 'Behöver informera om ändringen' },
  { value: 'set_informerad', label: 'Sätt som Informerad', description: 'Har redan meddelat kunden' }
]

export function RevertToPreliminaryModal({
  isOpen,
  onClose,
  assignmentNumber,
  assignmentTitle,
  assignmentDate,
  startTime,
  customerName,
  customerCurrentStatus,
  tgList,
  onConfirm
}: RevertToPreliminaryModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [customerAction, setCustomerAction] = useState<CustomerStatusAction>('reset_to_ej_kontaktad')
  const [sendSmsTo, setSendSmsTo] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    tgList.forEach(tg => {
      // Default to true if TG has phone number
      initial[tg.id] = !!tg.phone
    })
    return initial
  })
  const [extraMessages, setExtraMessages] = useState<Record<string, string>>({})
  const [expandedSms, setExpandedSms] = useState<Record<string, boolean>>({})

  const toggleSendSms = (tgId: string) => {
    setSendSmsTo(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const toggleSmsExpanded = (tgId: string) => {
    setExpandedSms(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const updateExtraMessage = (tgId: string, message: string) => {
    setExtraMessages(prev => ({ ...prev, [tgId]: message }))
  }

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm({
        customerAction,
        sendSmsTo,
        extraMessages
      })
      onClose()
    } catch (error) {
      console.error('Error reverting to preliminary:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const getAssignmentIdentifier = () => {
    if (assignmentNumber) {
      return `#${assignmentNumber} ${assignmentTitle}`
    }
    return assignmentTitle
  }

  const getCustomerStatusLabel = (status: CustomerCommStatus) => {
    switch (status) {
      case 'ej_kontaktad': return 'Ej kontaktad'
      case 'informerad': return 'Informerad'
      case 'bekraftad': return 'Bekräftad'
      case 'avbokad': return 'Avbokad'
      default: return status
    }
  }

  const getTgStatusLabel = (status: TgWorkStatus) => {
    switch (status) {
      case 'preliminar_tilldelad': return 'Preliminärt tilldelad'
      case 'tilldelad': return 'Tilldelad'
      case 'ej_rapporterad': return 'Ej rapporterad'
      case 'rapporterad': return 'Rapporterad'
      case 'komplettering': return 'Komplettering'
      case 'godkand': return 'Godkänd'
      case 'avsagt': return 'Avsagt'
      default: return status
    }
  }

  // Format time to HH:MM
  const formatTime = (time: string | null | undefined) => {
    if (!time) return ''
    return time.substring(0, 5)
  }

  // Get SMS message for TG
  const getSmsMessage = (tgId: string) => {
    const identifier = getAssignmentIdentifier()
    const dateTime = assignmentDate
      ? (startTime ? `${assignmentDate} kl ${formatTime(startTime)}` : assignmentDate)
      : ''
    const extra = extraMessages[tgId]?.trim()

    let message = `Uppdraget ${identifier}${dateTime ? ` (planerat ${dateTime})` : ''} har ändrats till preliminär planering.`
    if (extra) {
      message += ` ${extra}`
    } else {
      message += ' Vi återkopplar när vi vet mera.'
    }
    return message
  }

  // Count total SMS to be sent
  const getTotalSmsCount = () => {
    return tgList.filter(tg => tg.phone && sendSmsTo[tg.id]).length
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Ändra till preliminär
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  Uppdraget kommer att markeras som preliminärt
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
          {/* Assignment summary */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm text-nature-700 dark:text-nature-300">
              Är du säker på att du vill ändra uppdraget till preliminär status?
            </p>
            <p className="mt-2 font-medium text-nature-900 dark:text-nature-100">
              {getAssignmentIdentifier()}
            </p>
            {assignmentDate && (
              <p className="text-sm text-nature-600 dark:text-nature-400 mt-1">
                Planerat: {assignmentDate} {startTime ? `kl ${formatTime(startTime)}` : ''}
              </p>
            )}
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-3 italic">
              Alla TG:s återställs till "Preliminärt tilldelad"
            </p>
          </div>

          {/* Customer status section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-nature-700 dark:text-nature-300">
              <User className="h-4 w-4" />
              <span className="font-medium">Kundstatus</span>
              <span className="text-sm text-nature-500">
                ({customerName} - nuvarande: {getCustomerStatusLabel(customerCurrentStatus)})
              </span>
            </div>

            <div className="space-y-2 pl-6">
              {CUSTOMER_STATUS_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                    customerAction === option.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-nature-200 dark:border-nature-600 hover:border-nature-300 dark:hover:border-nature-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="customerAction"
                    value={option.value}
                    checked={customerAction === option.value}
                    onChange={() => setCustomerAction(option.value)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <p className={`text-sm font-medium ${
                      customerAction === option.value
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-nature-700 dark:text-nature-300'
                    }`}>
                      {option.label}
                    </p>
                    <p className="text-xs text-nature-500 dark:text-nature-400">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* TG notification section */}
          {tgList.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-nature-700 dark:text-nature-300">
                <Leaf className="h-4 w-4" />
                <span className="font-medium">Meddela trädgårdsmästare</span>
                <span className="text-sm text-nature-500">({tgList.length} tilldelade)</span>
              </div>

              <div className="space-y-3 pl-6">
                {tgList.map(tg => {
                  const canSendSms = !!tg.phone
                  const willSendSms = canSendSms && sendSmsTo[tg.id]
                  const isExpanded = expandedSms[tg.id]

                  return (
                    <div key={tg.id} className="border border-nature-200 dark:border-nature-700 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-nature-800 dark:text-nature-200">{tg.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400">
                            {getTgStatusLabel(tg.currentStatus)}
                          </span>
                        </div>
                        {/* SMS indicator */}
                        {canSendSms && (
                          <button
                            type="button"
                            onClick={() => toggleSmsExpanded(tg.id)}
                            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                              willSendSms
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                            }`}
                          >
                            <MessageSquare className="h-3 w-3" />
                            <span>{willSendSms ? 'SMS' : 'Inget SMS'}</span>
                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                        )}
                        {!tg.phone && (
                          <span className="text-xs text-nature-400 dark:text-nature-500 italic">
                            Inget telefonnummer
                          </span>
                        )}
                      </div>

                      {/* SMS Preview - collapsible */}
                      {canSendSms && isExpanded && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                              SMS till {tg.phone}
                            </span>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sendSmsTo[tg.id] || false}
                                onChange={() => toggleSendSms(tg.id)}
                                className="h-3.5 w-3.5 text-primary-600 rounded"
                              />
                              <span className="text-xs text-blue-600 dark:text-blue-400">Skicka</span>
                            </label>
                          </div>

                          {/* Extra info input */}
                          <div className="mb-2">
                            <input
                              type="text"
                              value={extraMessages[tg.id] || ''}
                              onChange={(e) => updateExtraMessage(tg.id, e.target.value)}
                              placeholder="Extra info (valfritt)..."
                              className="w-full px-2 py-1 text-xs border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100"
                            />
                          </div>

                          <div className={`bg-white dark:bg-nature-800 rounded p-2 border border-blue-200 dark:border-blue-700 ${!sendSmsTo[tg.id] ? 'opacity-50' : ''}`}>
                            <p className="text-xs text-nature-700 dark:text-nature-300 font-mono">
                              {getSmsMessage(tg.id)}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Show what will happen */}
                      <p className="text-sm text-nature-500 dark:text-nature-400 italic mt-2">
                        → Återställs till "Preliminärt tilldelad"
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {tgList.length === 0 && (
            <div className="text-center py-4 text-nature-500 dark:text-nature-400 text-sm italic">
              Inga trädgårdsmästare tilldelade
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* SMS summary */}
            <div className="text-sm text-nature-500 dark:text-nature-400">
              {getTotalSmsCount() > 0 ? (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  {getTotalSmsCount()} SMS skickas
                </span>
              ) : (
                <span className="italic">Inga SMS skickas</span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="btn-outline px-4 py-2"
                disabled={isSubmitting}
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="btn-primary px-4 py-2 flex items-center space-x-2 disabled:opacity-50 bg-amber-600 hover:bg-amber-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sparar...</span>
                  </>
                ) : (
                  <span>Ändra till preliminär</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
